import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Typography,
  LinearProgress,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Stop as StopIcon,
  RestartAlt as RestartIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
} from '@mui/icons-material';
import { formatBytes, formatUptime } from '../utils/formatters';

interface Process {
  pm_id: number;
  name: string;
  status: string;
  cpu: number;
  mem: number;
  uptime: number;
  restarts: number;
  pid: number;
  monit: {
    cpu: number;
    memory: number;
  };
}

interface ProcessListProps {
  processes: Process[];
  onProcessAction: (action: string, processId: string) => void;
  selectedProcess: Process | null;
  onSelectProcess: (process: Process | null) => void;
}

const ProcessList: React.FC<ProcessListProps> = ({
  processes,
  onProcessAction,
  selectedProcess,
  onSelectProcess,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [logs, setLogs] = useState<string>('');

  const toggleRowExpansion = (processId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(processId)) {
      newExpanded.delete(processId);
    } else {
      newExpanded.add(processId);
    }
    setExpandedRows(newExpanded);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'stopped':
        return 'error';
      case 'errored':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online':
        return 'Çalışıyor';
      case 'stopped':
        return 'Durduruldu';
      case 'errored':
        return 'Hata';
      default:
        return status;
    }
  };

  const handleViewLogs = async (processId: number) => {
    try {
      // Logları API'den al
      setLogsDialogOpen(true);
      setLogs('Loglar yükleniyor...');
      // Burada API çağrısı yapılacak
    } catch (error) {
      console.error('Log alma hatası:', error);
    }
  };

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Uygulama</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>CPU</TableCell>
              <TableCell>Bellek</TableCell>
              <TableCell>Çalışma Süresi</TableCell>
              <TableCell>Yeniden Başlatma</TableCell>
              <TableCell>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processes.map((process) => (
              <React.Fragment key={process.pm_id}>
                <TableRow hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => toggleRowExpansion(process.pm_id)}
                      >
                        {expandedRows.has(process.pm_id) ? <ExpandIcon /> : <CollapseIcon />}
                      </IconButton>
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {process.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(process.status)}
                      color={getStatusColor(process.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 100 }}>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={process.cpu}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {process.cpu.toFixed(1)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatBytes(process.mem)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatUptime(process.uptime)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {process.restarts}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Logları Görüntüle">
                        <IconButton
                          size="small"
                          onClick={() => handleViewLogs(process.pm_id)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Yeniden Başlat">
                        <IconButton
                          size="small"
                          onClick={() => onProcessAction('restart', process.pm_id.toString())}
                        >
                          <RestartIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Durdur">
                        <IconButton
                          size="small"
                          onClick={() => onProcessAction('stop', process.pm_id.toString())}
                        >
                          <StopIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sil">
                        <IconButton
                          size="small"
                          onClick={() => onProcessAction('delete', process.pm_id.toString())}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={expandedRows.has(process.pm_id)} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        <Typography variant="h6" gutterBottom component="div">
                          Detaylar
                        </Typography>
                        <Table size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell>PID</TableCell>
                              <TableCell>{process.pid}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>CPU Kullanımı</TableCell>
                              <TableCell>{process.cpu.toFixed(2)}%</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Bellek Kullanımı</TableCell>
                              <TableCell>{formatBytes(process.mem)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Yeniden Başlatma Sayısı</TableCell>
                              <TableCell>{process.restarts}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Loglar Dialogu */}
      <Dialog
        open={logsDialogOpen}
        onClose={() => setLogsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Uygulama Logları</DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              backgroundColor: 'background.paper',
              p: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              maxHeight: 400,
              overflow: 'auto',
            }}
          >
            {logs}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsDialogOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcessList;
